import {
  configApiRef,
  discoveryApiRef,
  useApi,
} from "@backstage/core-plugin-api";
import type { DiscoveryApi } from "@backstage/core-plugin-api";
import { useCallback, useEffect, useState } from "react";

import { isAppProxyActive } from "../../appProxy";

const SESSION_EXPIRED_EVENT = "backstage:session-expired";
export const RETRY_DELAY_MS = 2000;

let isGlobalSessionExpired = false;

/**
 * Emite el evento de sesión caducada para notificar a la aplicación de forma global.
 */
export function notifySessionExpired(): void {
  if (isGlobalSessionExpired) {
    return;
  }
  isGlobalSessionExpired = true;
  if (
    typeof window !== "undefined" &&
    typeof window.dispatchEvent === "function"
  ) {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

/**
 * Restablece el estado de sesión caducada (usado en tests o reautenticación).
 */
export function resetSessionExpired(): void {
  isGlobalSessionExpired = false;
}

/**
 * Devuelve el valor actual de sesión caducada.
 */
export function getSessionExpired(): boolean {
  return isGlobalSessionExpired;
}

/**
 * Hook reactivo para suscribirse al estado global de sesión caducada.
 */
export function useSessionExpired(): boolean {
  const [expired, setExpired] = useState(isGlobalSessionExpired);

  useEffect(() => {
    if (isGlobalSessionExpired && !expired) {
      setExpired(true);
    }

    const handleExpired = () => {
      setExpired(true);
    };

    if (typeof window !== "undefined") {
      window.addEventListener(SESSION_EXPIRED_EVENT, handleExpired);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpired);
      }
    };
  }, [expired]);

  return expired;
}

export type AppProxyProbeStatus = "valid" | "expired" | "unavailable";

export interface AppProxyProbeResult {
  status: AppProxyProbeStatus;
  reason?: string;
}

// Detección de App Proxy centralizada en src/appProxy.ts (origin igual a
// app.baseUrl + flag app.oauth2ProxyEnabled). Reexportada para mantener el
// contrato público existente de este módulo.
export { isAppProxyActive } from "../../appProxy";

export const APP_PROXY_HOST_SUFFIX = ".msappproxy.net";

export const OAUTH2_PROXY_REFRESH_PATH = "/oauth2Proxy/refresh";

const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308]);

/**
 * True solo si el host de destino del redirect está en la allowlist de login
 * corporativo: el login de Entra ID (exacto) o un host de Azure App Proxy.
 * Los paths (/login, /signin, ...) NO bastan: cualquier otro dominio con
 * path de login es NO demostrable => "unavailable".
 */
function isDemonstrableLoginRedirectHost(hostname: string): boolean {
  return (
    hostname === "login.microsoftonline.com" ||
    hostname.endsWith(APP_PROXY_HOST_SUFFIX)
  );
}

function hostnameOf(url: string): string | undefined {
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
}

/**
 * Probe REAL de sesión contra el backend de auth de Backstage.
 * A diferencia de identityApi.getCredentials() (que puede devolver un token
 * cacheado sin validar la cookie de App Proxy), este endpoint fuerza el
 * refresh con la cookie. Mantiene `credentials: "include"` y
 * `X-Requested-With: XMLHttpRequest` porque es exactamente el contrato que
 * la implementación real ProxiedSignInIdentity (core-components,
 * ProxiedSignInPage) usa contra /refresh.
 *
 * Clasificación (diagnóstico, no renueva identidad Backstage):
 * - valid:       2xx + JSON parseable + backstageIdentity.token no vacío.
 * - expired:     401, o redirect demostrable hacia login de Entra ID / App
 *                Proxy (allowlist de hosts; el path no basta), o redirect
 *                opaco (opaqueredirect / status 0): con `redirect: "manual"`
 *                el browser oculta el destino cross-origin, y como el probe
 *                solo corre contra hosts de App Proxy (gating en los
 *                callers), un redirect opaco solo puede ser hacia el login
 *                corporativo.
 * - unavailable: 403 (RBAC/NotAllowedError del catálogo no prueba expiración),
 *                5xx, 429, otros 4xx, red/discovery, HTML 200, JSON
 *                inválido, redirects explícitos no demostrables.
 */
export async function probeAppProxySession(
  discoveryApi: DiscoveryApi,
): Promise<AppProxyProbeResult> {
  let baseUrl: string;
  let authBaseHost: string | undefined;
  try {
    baseUrl = await discoveryApi.getBaseUrl("auth");
    authBaseHost = hostnameOf(baseUrl);
  } catch {
    return { status: "unavailable", reason: "discovery-failed" };
  }

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${OAUTH2_PROXY_REFRESH_PATH}`, {
      method: "GET",
      credentials: "include",
      headers: { "X-Requested-With": "XMLHttpRequest" },
      // redirect: "manual": cuando App Proxy redirige el refresh hacia
      // login.microsoftonline.com, el follow-mode nativo muere por CORS
      // (el fetch rechaza y se ve como network-error => unavailable).
      // Con manual recibimos un opaqueredirect sin exponer el destino, que
      // clasificamos como "expired" (ver branch de abajo).
      redirect: "manual",
    });
  } catch {
    return { status: "unavailable", reason: "network-error" };
  }

  // Redirect opaco (redirect: manual / status 0): el browser oculta el
  // destino cross-origin. Antes era "unavailable" por no demostrable, pero
  // con follow-mode este caso moría por CORS (network-error) y hoy solo
  // ocurre cuando App Proxy redirige a login (probe acotado a hosts App
  // Proxy por gating en los callers) => "expired".
  if (response.type === "opaqueredirect" || response.status === 0) {
    return { status: "expired", reason: "opaque-redirect-to-login" };
  }

  // 3xx explícito con Location (defensivo; con redirect: "manual" solo
  // llega un redirect same-origin, cross-origin es opaqueredirect).
  // Precaución: el mismo host que la base de auth
  // NO prueba expiración; solo un host allowlist DISTINTO.
  if (REDIRECT_STATUSES.has(response.status)) {
    const location = response.headers.get("location") ?? "";
    const locationHost = hostnameOf(location);
    if (
      location &&
      locationHost &&
      locationHost !== authBaseHost &&
      isDemonstrableLoginRedirectHost(locationHost)
    ) {
      return { status: "expired", reason: "redirected-to-login" };
    }
    return { status: "unavailable", reason: "redirect-not-demonstrable" };
  }

  // Defensivo: si alguna capa devolviera la respuesta final tras seguir un
  // redirect (manual-mode no debería, pero jsdom/browsers varían), se compara
  // el host final con el host de la base de auth: si es el mismo, no es
  // prueba de expiración y se continúa con la validación normal
  // (status/content-type/JSON/token => valid | unavailable). Solo un host
  // final DISTINTO de la allowlist (login.microsoftonline.com o un host
  // *.msappproxy.net permitido) es "expired"; cualquier otro es
  // "unavailable".
  if (response.redirected) {
    const finalHost = hostnameOf(response.url);
    if (finalHost && finalHost !== authBaseHost) {
      if (isDemonstrableLoginRedirectHost(finalHost)) {
        return { status: "expired", reason: "redirected-to-login" };
      }
      return { status: "unavailable", reason: "redirect-not-demonstrable" };
    }
  }

  if (response.status === 401) {
    return { status: "expired", reason: "http-401" };
  }

  // 403 de catálogo/RBAC/NotAllowedError no prueba sesión expirada.
  if (response.status < 200 || response.status > 299) {
    return { status: "unavailable", reason: `http-${response.status}` };
  }

  // 2xx: exigir JSON con identidad; HTML 200 (login tras redirect) no vale.
  const contentType = (
    response.headers.get("content-type") ?? ""
  ).toLowerCase();
  if (contentType && !contentType.includes("json")) {
    return { status: "unavailable", reason: `content-type-${contentType}` };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { status: "unavailable", reason: "invalid-json" };
  }

  const token = (payload as { backstageIdentity?: { token?: unknown } })
    ?.backstageIdentity?.token;
  if (typeof token === "string" && token.length > 0) {
    return { status: "valid" };
  }
  return { status: "unavailable", reason: "missing-identity-token" };
}

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

// Single-flight por instancia de DiscoveryApi: un probe en vuelo de otra
// instancia (otro holder) nunca se reemplaza ni se comparte por error.
let sharedProbes = new WeakMap<DiscoveryApi, Promise<AppProxyProbeResult>>();

/**
 * Restablece el single-flight compartido (uso en tests): reemplaza el WeakMap
 * y descarta todas las entradas.
 */
export function resetAppProxyProbe(): void {
  sharedProbes = new WeakMap<DiscoveryApi, Promise<AppProxyProbeResult>>();
}

/**
 * Orquestador compartido por useAppProxySessionGuard y
 * AppProxyAwareNotFoundPage:
 * - single-flight: si ya hay un probe en vuelo para esta discoveryApi, se
 *   une a él (no duplica request ni retry). La entrada se limpia al
 *   finalizar para que el siguiente probe sea fresco.
 * - retry: solo reintenta cuando el primer intento es "unavailable"
 *   (ambiguo). "valid" y "expired" son definitivos.
 * - notify: SOLO un resultado final "expired" publica la expiración global;
 *   "unavailable" nunca lo hace (backend caído, HTML 200, etc.).
 */
export function requestAppProxyProbe(
  discoveryApi: DiscoveryApi,
): Promise<AppProxyProbeResult> {
  const inFlight = sharedProbes.get(discoveryApi);
  if (inFlight) {
    return inFlight;
  }

  const probe = (async () => {
    const first = await probeAppProxySession(discoveryApi);
    if (first.status !== "unavailable") {
      return first;
    }
    await wait(RETRY_DELAY_MS);
    return probeAppProxySession(discoveryApi);
  })();

  sharedProbes.set(discoveryApi, probe);

  void probe.then((result) => {
    if (result.status === "expired") {
      notifySessionExpired();
    }
    if (sharedProbes.get(discoveryApi) === probe) {
      sharedProbes.delete(discoveryApi);
    }
  });

  return probe;
}

export interface UseAppProxySessionGuardOptions {
  /**
   * Heartbeat opt-in (uso interno/test). Por defecto 0: SIN intervalo; la
   * comprobación solo ocurre al recuperar visibilidad, al montar el 404
   * (AppProxyAwareNotFoundPage) o vía checkCredentials manual. La renovación
   * normal de identidad Backstage no pasa por aquí.
   */
  pollingIntervalMs?: number;
}

/**
 * Guard de la sesión de Azure AD / Entra Application Proxy, estrictamente
 * acotado a: app.oauth2ProxyEnabled === true && window.location.origin
 * igual al app.baseUrl público del entorno (detección centralizada en
 * src/appProxy.ts, sin allowlists hardcodeadas).
 *
 * Sin heartbeat por defecto (el sondeo raw cada 30s se eliminó: los timers
 * no lanzan requests en configuración normal). Solo hay comprobación al
 * recuperar visibilidad, cuando aparece el 404, o con pollingIntervalMs
 * explícito positivo (interno/test). Cada comprobación usa el orquestador
 * compartido requestAppProxyProbe (single-flight + retry + notificación
 * centralizada); la renovación normal de tokens de Backstage no se toca.
 */
export function useAppProxySessionGuard(
  options: UseAppProxySessionGuardOptions = {},
): {
  isExpired: boolean;
  checkCredentials: () => Promise<AppProxyProbeResult>;
} {
  const { pollingIntervalMs = 0 } = options;
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const isExpired = useSessionExpired();

  const appProxyActive = isAppProxyActive(
    configApi,
    typeof window !== "undefined" ? window.location.origin : "",
  );

  const checkCredentials =
    useCallback(async (): Promise<AppProxyProbeResult> => {
      if (isGlobalSessionExpired || !appProxyActive) {
        return { status: "unavailable", reason: "app-proxy-not-active" };
      }
      return requestAppProxyProbe(discoveryApi);
    }, [discoveryApi, appProxyActive]);

  // Comprobación al recuperar visibilidad: siempre activa mientras el guard
  // esté activo (independiente del heartbeat opt-in).
  useEffect(() => {
    if (!appProxyActive || isExpired) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkCredentials();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [appProxyActive, isExpired, checkCredentials]);

  // Heartbeat opt-in: solo un pollingIntervalMs explícito positivo crea un
  // interval (configuración normal de producción: cero timers).
  useEffect(() => {
    if (
      !appProxyActive ||
      isExpired ||
      !pollingIntervalMs ||
      pollingIntervalMs <= 0
    ) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      void checkCredentials();
    }, pollingIntervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [appProxyActive, isExpired, pollingIntervalMs, checkCredentials]);

  return {
    isExpired,
    checkCredentials,
  };
}
